import React, { useMemo, useState } from 'react';
import { opsApi, type SizeServer } from '../../../api/dbOps';
import { errorText } from '../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Kpi, Panel, Pill, Spinner, bytes } from '../runner/ui';
import { Empty, ServerPicker, num, td, th, usePoll } from './shared';

/**
 * Size and growth (approved mockup "Database redesign", Size and growth):
 * disk per server (when the server can tell), the biggest companies and
 * tables, 30-day growth from the nightly snapshots, archive suggestions.
 */
const SizeTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const [server, setServer] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const servers = usePoll(() => opsApi.servers(), null);
  const size = usePoll(() => opsApi.size(server || undefined), null, [server]);

  const growthByTable = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of size.data?.growth?.tables || []) m.set(`${t.db}.${t.name}`, t.added_bytes);
    return m;
  }, [size.data]);
  const perDayByDb = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of size.data?.growth?.databases || []) if (d.per_day_bytes) m.set(d.db, d.per_day_bytes);
    return m;
  }, [size.data]);

  const snapshot = async () => {
    setSaving(true);
    try {
      const r = await opsApi.snapshot();
      setMsg(`Stored today's size of ${r.rows} tables.`);
      void size.reload();
    } catch (err) {
      setMsg(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const tables = (size.data?.servers || []).flatMap((s) => s.tables).sort((a, b) => b.total_bytes - a.total_bytes).slice(0, 25);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ServerPicker servers={servers.data || []} value={server} onChange={setServer} />
        <span className="flex-1" />
        {size.loading && <Spinner />}
        {isOwner && <Btn kind="ghost" small disabled={saving} onClick={snapshot}>Store today's sizes now</Btn>}
      </div>
      {msg && <Alert tone="info" title={msg} action={<Btn kind="ghost" small onClick={() => setMsg(null)}>Dismiss</Btn>} />}
      {size.error && !size.data ? <ErrorBox>{errorText(size.error)}</ErrorBox> : null}

      {(size.data?.servers || []).map((s) => <ServerKpis key={s.server.key} s={s} perDayByDb={perDayByDb} />)}

      {size.data && !size.data.growth_available && (
        <Alert tone="info" title="Growth shows after a few nightly snapshots">
          Sizes are stored every night by the scheduler (needs the registry migration migrations-2026-10-11-db-ops). Until then this page shows today's sizes only.
        </Alert>
      )}

      <Panel title="Biggest tables" bodyClass="p-0">
        {tables.length === 0 ? <div className="p-4 text-[13px] text-slate-500">{size.loading ? 'Loading…' : 'No tables found.'}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50"><tr><th className={th}>Table</th><th className={th}>Size</th><th className={th}>Rows</th><th className={th}>30-day growth</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {tables.map((t) => {
                  const g = growthByTable.get(`${t.db}.${t.name}`);
                  return (
                    <tr key={`${t.db}.${t.name}`}>
                      <td className={td}><span className="font-medium text-slate-900">{t.db}</span>.{t.name}</td>
                      <td className={`${td} tabular-nums`}>{bytes(t.total_bytes)}</td>
                      <td className={`${td} tabular-nums`}>{num(t.row_estimate)}</td>
                      <td className={td}>{g === undefined ? <span className="text-slate-400">—</span> : g > 100 * 1024 * 1024 ? <Pill tone="warn">+{bytes(g)}</Pill> : `+${bytes(g)}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {(size.data?.suggestions || []).map((s) => (
        <Alert key={`${s.db}.${s.table}`} tone="warn" title={`Suggestion: ${s.db}.${s.table}`}>
          {s.reason} {s.suggestion} The Retention tab handles the tables it knows about.
        </Alert>
      ))}
      {size.data && !size.data.servers.length && <Empty>No database servers found.</Empty>}
    </div>
  );
};

const ServerKpis: React.FC<{ s: SizeServer; perDayByDb: Map<string, number> }> = ({ s, perDayByDb }) => {
  if (!s.ok) return <Alert tone="bad" title={`${s.server.label} is unreachable`}>{s.error}</Alert>;
  const perDay = s.databases.reduce((sum, d) => sum + (perDayByDb.get(d.db) || 0), 0);
  const months = s.disk && perDay > 0 ? Math.round(s.disk.free_bytes / perDay / 30) : null;
  const biggest = s.databases[0];
  return (
    <div>
      <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wider text-slate-500">{s.server.label}</div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Disk" value={s.disk?.percent !== null && s.disk?.percent !== undefined ? `${s.disk.percent}% used` : 'Unknown'} tone={s.disk?.percent && s.disk.percent >= 80 ? 'warn' : 'plain'} foot={s.disk ? undefined : 'Server does not report disk (DISKS plugin)'} />
        <Kpi label="Free" value={s.disk ? bytes(s.disk.free_bytes) : '—'} />
        <Kpi label="Full in about" value={months === null ? '—' : months > 120 ? '10+ years' : months >= 1 ? `${months} months` : 'under a month'} tone={months !== null && months < 3 ? 'bad' : 'plain'} foot={perDay ? `+${bytes(perDay)} a day` : undefined} />
        <Kpi label={s.server.kind === 'registry' ? 'Registry database' : 'Biggest company'} value={biggest ? bytes(biggest.total_bytes) : '—'} foot={biggest ? (biggest.company?.name || biggest.db) : undefined} />
      </div>
    </div>
  );
};

export default SizeTab;
